import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, TrendingDown, Package } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { DealCard } from '../components/DealCard';
import { PackageCard } from '../components/PackageCard';
import { SearchAndFilter } from '../components/SearchAndFilter';
import { QuoteForm } from '../components/QuoteForm';
import { promotionsAPI } from '../lib/api';
import { getContinentByDestination } from '../lib/helpers';

const Home = () => {
  const [promotions, setPromotions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filteredPromotions, setFilteredPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', continent: null });

  const loadPromotions = useCallback(async () => {
    try {
      const response = await promotionsAPI.getAll();
      const allData = response.data;
      const flights = allData.filter(p => !p.is_package);
      const pkgs = allData.filter(p => p.is_package);
      setPromotions(flights);
      setFilteredPromotions(flights);
      setPackages(pkgs);
    } catch {
      // silently handled - UI shows empty state
    } finally {
      setLoading(false);
    }
  }, []);

  const filterPromotions = useCallback(() => {
    let filtered = [...promotions];

    if (filters.search) {
      filtered = filtered.filter(promo =>
        promo.destination.toLowerCase().includes(filters.search.toLowerCase()) ||
        promo.origin.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.continent) {
      filtered = filtered.filter(promo =>
        getContinentByDestination(promo.destination) === filters.continent
      );
    }

    setFilteredPromotions(filtered);
  }, [promotions, filters]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  useEffect(() => {
    filterPromotions();
  }, [filterPromotions]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <section className="hero-bg pt-24 sm:pt-32 pb-16 sm:pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center text-white"
          >
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight mb-4 sm:mb-6" data-testid="hero-title">
              Encontre passagens aéreas com
              <br />
              <span className="text-[#00A8B5]">até 60% de desconto</span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-slate-100 mb-6 sm:mb-8 max-w-2xl mx-auto px-4" data-testid="hero-subtitle">
              Promoções reais encontradas todos os dias através de oportunidades com milhas
            </p>
            <a href="#promocoes">
              <button 
                className="bg-[#00A8B5] hover:bg-[#008C99] text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 inline-flex items-center gap-2 text-sm sm:text-base"
                data-testid="hero-cta-button"
              >
                Ver Promoções
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#00A8B5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingDown className="w-8 h-8 text-[#00A8B5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Economia Real</h3>
              <p className="text-slate-600">Economize até 60% comparado com preços convencionais</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#00A8B5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingDown className="w-8 h-8 text-[#00A8B5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Promoções Diárias</h3>
              <p className="text-slate-600">Novas oportunidades encontradas todos os dias</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#00A8B5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingDown className="w-8 h-8 text-[#00A8B5]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Comparação Transparente</h3>
              <p className="text-slate-600">Compare com preços do Skyscanner</p>
            </div>
          </div>
        </div>
      </section>

      <section id="promocoes" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="promotions-title">
              Promoções de Voos
            </h2>
            <p className="text-lg text-slate-600">
              Explore as melhores oportunidades de voos com milhas
            </p>
          </div>

          <SearchAndFilter 
            onFilterChange={setFilters}
            selectedContinent={filters.continent}
          />

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A8B5]"></div>
            </div>
          ) : filteredPromotions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600" data-testid="no-promotions">Nenhuma promoção encontrada</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="promotions-grid">
              {filteredPromotions.map((promotion, index) => (
                <DealCard key={promotion.id} promotion={promotion} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {packages.length > 0 && (
        <section id="pacotes" className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#00A8B5]/10 text-[#00A8B5] px-3 py-1 rounded-full text-sm font-medium mb-3">
                  <Package className="w-4 h-4" />
                  Voo + Hospedagem
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="packages-section-title">
                  Pacotes Completos
                </h2>
                <p className="text-slate-600 mt-2">
                  Viaje com tudo incluso: voo, hotel e serviços extras
                </p>
              </div>
              <Link 
                to="/pacotes"
                className="text-[#00A8B5] hover:text-[#008C99] font-semibold text-sm flex items-center gap-1 transition"
                data-testid="view-all-packages-link"
              >
                Ver todos os pacotes
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-packages-grid">
              {packages.map((pkg, idx) => (
                <PackageCard key={pkg.id} promotion={pkg} index={idx} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="cotacao" className="py-16 px-4 bg-slate-100">
        <div className="max-w-3xl mx-auto">
          <QuoteForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
